import React, { useEffect, useState } from 'react';
import { Clock, CheckCircle, XCircle, AlertCircle, User, Calendar } from 'lucide-react';
import supportTicketService from '../../services/supportTicketService';
import toast from 'react-hot-toast';

const AllSupports = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTickets();
  }, []);

  const fetchTickets = async () => {
    try {
      const response = await supportTicketService.getAllTickets();
      setTickets(response.data.data || response.data || []);
    } catch (error) {
      toast.error('Failed to load tickets');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (ticketId, status) => {
    try {
      await supportTicketService.updateTicketStatus(ticketId, status);
      toast.success('Status updated');
      fetchTickets();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const columns = [
    { id: 'open', title: 'Open', color: 'bg-blue-50', borderColor: 'border-blue-200', icon: AlertCircle },
    { id: 'in_progress', title: 'In Progress', color: 'bg-yellow-50', borderColor: 'border-yellow-200', icon: Clock },
    { id: 'resolved', title: 'Resolved', color: 'bg-green-50', borderColor: 'border-green-200', icon: CheckCircle },
    { id: 'closed', title: 'Closed', color: 'bg-gray-50', borderColor: 'border-gray-200', icon: XCircle }
  ];

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-700',
      high: 'bg-orange-100 text-orange-700',
      medium: 'bg-blue-100 text-blue-700',
      low: 'bg-gray-100 text-gray-700'
    };
    return colors[priority] || colors.medium;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">All Support Tickets</h1>
        <p className="text-gray-600 mt-1">Manage and track all support requests</p>
      </div>

      <div className="overflow-x-auto pb-6">
        <div className="flex gap-4 min-w-max">
          {columns.map((column) => {
            const Icon = column.icon;
            const columnTickets = tickets.filter(t => t.status === column.id);

            return (
              <div key={column.id} className="flex-shrink-0 w-80">
                <div className={`${column.color} border-2 ${column.borderColor} rounded-xl overflow-hidden`}>
                  <div className="bg-gradient-to-r from-gray-700 to-gray-800 p-4 text-white">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon className="h-5 w-5" />
                        <h3 className="font-bold text-sm">{column.title}</h3>
                      </div>
                      <span className="bg-white/20 px-3 py-1 rounded-full text-sm font-bold">
                        {columnTickets.length}
                      </span>
                    </div>
                  </div>

                  <div className="p-3 space-y-3 max-h-[calc(100vh-280px)] overflow-y-auto">
                    {columnTickets.length === 0 ? (
                      <div className="text-center py-12 text-gray-400">
                        <Icon className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p className="text-sm font-medium">No tickets</p>
                      </div>
                    ) : (
                      columnTickets.map((ticket) => (
                        <div
                          key={ticket.id}
                          className="bg-white rounded-xl border border-gray-200 p-4 hover:shadow-lg transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className={`px-2 py-1 rounded text-xs font-semibold uppercase ${getPriorityColor(ticket.priority)}`}>
                              {ticket.priority}
                            </span>
                            <span className="text-xs text-gray-500">#{ticket.id}</span>
                          </div>

                          <h4 className="text-sm font-semibold text-gray-900 mb-2 line-clamp-2">
                            {ticket.subject}
                          </h4>

                          <p className="text-xs text-gray-600 mb-3 line-clamp-2">
                            {ticket.description}
                          </p>

                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                            <User className="h-3 w-3" />
                            <span>{ticket.requester_name || 'Unknown'}</span>
                          </div>

                          <div className="flex items-center gap-2 text-xs text-gray-500 mb-3">
                            <Calendar className="h-3 w-3" />
                            <span>{new Date(ticket.created_at).toLocaleDateString()}</span>
                          </div>

                          <select
                            value={ticket.status}
                            onChange={(e) => updateStatus(ticket.id, e.target.value)}
                            className="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
                          >
                            <option value="open">Open</option>
                            <option value="in_progress">In Progress</option>
                            <option value="resolved">Resolved</option>
                            <option value="closed">Closed</option>
                          </select>
                        </div>
                      ))
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default AllSupports;
